import Game from '@/game/Game';
import { t } from '@/texts';
import { Stone } from '@/game/Board';

export default class Session {
  constructor(game, player, watcher) {
    this.game = game;
    this.player = player; // TODO: remove?

    this.revision = game.revision || 0;
    this.disposed = false;
    this.frozen = false;
    this.lifecycle = () => this.reportVisibility();
    this.freeze = () => { this.frozen = true; this.reportVisibility(); };
    this.resume = () => { this.frozen = false; this.reportVisibility(); this.sendPing(); this.reportSeen(); };
    document.addEventListener('visibilitychange', this.lifecycle);
    document.addEventListener('freeze', this.freeze);
    document.addEventListener('resume', this.resume);
    window.addEventListener('pagehide', this.freeze);
    window.addEventListener('pageshow', this.resume);
    this.confirmedMoves = game.moves.length;
    this.watcher = watcher;
    this.lastPong = 0;
    this.isCounting = false;
    this.countResult = null;

    this.game.addWatcher(this);
    this.reset();
  }

  keepalive() {
    if (this.disposed) return;
    this.sendPing();
    if(this.unixTime() - this.lastPong > 10)
      this.reset();
  }

  takeBackForOpponent() {
    // The '.set' record lays out the setup; it is never taken back.
    if(!this.game.lastMove || this.game.lastMove.move === '.set')
      return false;

    if(this.game.moves.length !== this.confirmedMoves)
      return false;

    this.sendTakeBackForOpponent(this.game.lastMove);
    this.confirmedMoves--;
    this.game.takeBack();
    return true;
  }

  reset() {
    console.log("resetting session");

    this.noticeAttempt = null;
    this.expectNotice = false;
    if(this.ws) {
      this.ws.onmessage = this.ws.onclose = this.ws.onerror = null;
      this.ws.close();
    }
    this.ws = new WebSocket(this.#wsUri());
    this.ws.onopen = this.onWsOpen.bind(this);
    this.ws.onmessage = this.onMessage.bind(this);
    this.ws.onclose = this.onWsClose.bind(this);
    this.ws.onerror = this.onWsError.bind(this);

    this.lastPong = this.unixTime();
    console.log("reset session");
  }

  send(msg) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const m = Object.assign({}, msg, { game: this.game.id });
    if(m.code !== "ping")
      console.log(`<=== ${JSON.stringify(m)}`);
    this.ws.send(JSON.stringify(m));
  }

  unixTime() {
    return Math.floor(Date.now() / 1000);
  }

  sendPing() {
    this.send({
      code: "ping",
      message: `${this.unixTime()}`
    });
  }

  sendStatus() {
    this.sendCode("status");
  }

  async resign() {
    if(this.isCounting) return false;

    const ok = await this.watcher.confirm(t('resign-confirm'));
    if(ok) this.game.resign();
  }

  startCount() {
    if(this.isCounting) return false;

    this.isCounting = true;
    this.sendCountRequest(this.player === Stone.BOTH ? this.game.nextPlayer : this.player);
  }

  sendCountRequest(stone) {
    this.sendReqWithMove("count-request", { stone, index: this.game.moves.length });
  }

  sendCountConfirm() {
    if(this.player === Stone.BLACK || this.player == Stone.WHITE)
      this.sendReqWithMove("count-confirm", { stone: this.player });
    else if(this.player === Stone.BOTH) {
      this.sendReqWithMove("count-confirm", { stone: Stone.BLACK });
      this.sendReqWithMove("count-confirm", { stone: Stone.WHITE });
    }
  }

  sendCountCancel() {
    this.sendCode("count-cancel");
  }

  sendCode(code) {
    this.send({code});
  }

  sendWatch(index) {
    this.send({
      code: "watch",
      move: { index }
    });
  }

  sendMove(move) {
    this.sendReqWithMove("move", move);
  }

  sendTakeBackForOpponent(move) {
    this.sendReqWithMove("take-back-for-opponent", move);
  }

  static convertMove(m, indexOffset=0) {
    const move = {
      index: m.index + indexOffset,
      move: m.move,
      time: m.time
    };

    if(m.stone)
      move.stone = Symbol.keyFor(m.stone);

    return move;
  }

  sendReqWithMove(code, m) {
    const move = Session.convertMove(m);
    this.send({ code, move });
  }

  onMove(move) {
    console.log(`session.onMove: ${JSON.stringify(move)}, this.confirmedMoves = ${this.confirmedMoves}`);

    if(move.index >= this.confirmedMoves) {
      // The server requests the reminder when it saves the stone; this page only warns when it fails.
      if (this.expectNotice && !move.move.startsWith('.')) {
        this.noticeAttempt = { index: move.index, move: move.move, revision: null, moveCount: move.index + 1, warned: false };
        this.expectNotice = false;
      }
      this.sendMove(move);
    }
  }

  onMessage(e) {
    const msg = JSON.parse(e.data);
    if(msg.code !== "pong")
      console.log(`---> ${JSON.stringify(msg)}`);

    if (['revision', 'move', 'seek', 'status'].includes(msg.code) && Number.isInteger(msg.revision)) {
      // A confirmed stone's warning belongs to its revision; a stone waiting for confirmation gets it then.
      if (msg.revision !== this.revision && this.noticeAttempt && this.noticeAttempt.revision !== null) this.noticeAttempt = null;
      this.revision = msg.revision;
    }
    switch(msg.code) {
      // Sent once on each connection: the page may have missed chat messages while disconnected.
      case 'revision': this.reportSeen(); if (this.watcher.onJoined) this.watcher.onJoined(); break;
      case 'chat': if (this.watcher.onChat) this.watcher.onChat(msg.chat); break;
      case 'notifiable': if (this.watcher.onNotifiable) this.watcher.onNotifiable(msg); break;
      case 'presence': if (this.watcher.onPresence) this.watcher.onPresence(msg); break;
      case 'notice':
        this.onNoticeMessage(msg);
        break;
      case "pong":
        this.onPongMessage(msg.message);
        break;
      case "status":
        this.onStatusMessage(msg);
        break;
      case "joined":
        break;
      case "move":
        this.onMoveMessage(msg.move);
        this.reportSeen();
        break;
      case "seek":
        this.noticeAttempt = null;
        this.onSeekMessage(msg.move.index);
        this.reportSeen();
        break;
      case "count-result":
        this.onCountResultMessage(msg);
        break;
      case "error":
        this.onErrorMessage(msg);
        break;
    }
  }

  onPongMessage(msg) {
    const unix = parseInt(msg);

    if(unix > this.lastPong)
      this.lastPong = unix;
  }

  onStatusMessage(msg) {
    const t = msg.message.split('|');
    switch(t[0]) {
      case 'play':
        this.isCounting = false;
        this.watcher.onPlay();
        break;
      case 'completed':
        this.isCounting = false;
        this.watcher.onCompleted();
        break;
      // A move is sealed (plan 2026-10-29-sealed-moves.md): sealed|<colour>|<records>[|requested-<colour>].
      case 'sealed':
        this.isCounting = false;
        if(this.watcher.onSealed) this.watcher.onSealed(Stone.parse(t[1]), t[3] ? Stone.parse(t[3].split('-')[1]) : null);
        break;
      case 'count':
        this.isCounting = true;
        this.onCountStatus(this.readDashed('black', t[1]), this.readDashed('white', t[2]));
        break;
    }
  }

  readDashed(key, str) {
    const t = str.split('-');
    if(t.length === 2 && t[0] === key)
      return t[1];
    else
      return null;
  }

  onCountStatus(black, white) {
    if(black === 'confirmed')
      this.onCountStatusConfirmedBy(Stone.BLACK);
    else if(white === 'confirmed')
      this.onCountStatusConfirmedBy(Stone.WHITE);
    else if(black === 'requested' && white === 'requested')
      this.onCountStatusConfirmedBy(Stone.NONE);
    else if(black === 'requested')
      this.onCountStatusRequestedBy(Stone.BLACK);
    else if(white === 'requested')
      this.onCountStatusRequestedBy(Stone.WHITE);
    else
      console.log(`count status ERROR: black-${black} white-${white}`);
  }

  async onCountStatusRequestedBy(stone) {
    if(!this.player) return;  // spectators: no counting dialogs
    if(this.player === Game.invert(stone) || this.player === Stone.BOTH) {
      const ok = await this.watcher.onCountRequest(stone);
      if(ok) {
        this.sendCountRequest(Game.invert(stone));
      } else {
        this.sendCountCancel();
      }
    } else {
      await this.watcher.onCountRequestSent(stone);
      // cancelled
      this.sendCountCancel();
    }
  }

  onCountStatusConfirmedBy(stone) {
    if(!this.player) return;
    if(stone === Stone.NONE || this.player === Stone.BOTH || stone === Game.invert(this.player)) {
      if(!this.countResult)
        this.sendCode("count-result");
    } else {
      this.watcher.onCountConfirmSent(stone);
    }
  }

  async onCountResultMessage(msg) {
    this.countResult = msg.estimate.countResult;
    if(!this.player) return;

    const ok = await this.watcher.onCountResult(msg.estimate.countResult);
    if(ok) {
      this.sendCountConfirm();
    } else {
      this.sendCountCancel();
    }
  }

  onMoveMessage(move) {
    if(move.index !== this.confirmedMoves) {
      console.log(`WARNING: ignore server move ${move.move} ` +
        `(index ${move.index} != confirmed ${this.confirmedMoves})`);
      return;
    }

    if(move.index > this.game.moves.length) {
      console.log(`WARNING: ignore server move ${move.move} ` +
        `(index ${move.index} > moves.length ${this.game.moves.length})`);
      return;
    }

    this.confirmedMoves++;
    move.stone = Stone.parse(move.stone);

    if(move.index === this.game.moves.length) {  // TODO: maybe call makeMove directly? which then checks the index?
      this.game.makeMove(move);
    } else {
      if(move.move !== this.game.moves[move.index].move) {
        console.log(`WARNING: reset index to ${move.index}`);
        this.resetMovesTo(move.index);
        this.game.makeMove(move);
      }
    }
    const attempt = this.noticeAttempt;
    if (attempt && attempt.revision === null && attempt.index === move.index) {
      if (attempt.move === move.move) attempt.revision = this.revision;
      else this.noticeAttempt = null;
    }
  }

  onSeekMessage(count) {
    if(count < 0 || count > this.confirmedMoves) {
      console.log(`WARNING: ignore server seek ${count}`);
      return;
    }

    this.resetMovesTo(count);
    this.confirmedMoves = count;
  }

  onErrorMessage(msg) {
    if(!msg.message) {
      console.log(`ERROR: unknown error msg ${JSON.stringify(msg)}`);
      return;
    }

    const parts = msg.message.split('|');
    const e = parts[0];
    
    switch(e) {
      case 'invalid-move':
        // Only a refused stone ends the expectation of its reminder warning.
        this.noticeAttempt = null;
        this.expectNotice = false;
        if(!msg.move || !('index' in msg.move)) {
          console.log(`ERROR: invalid invalid-move error msg ${JSON.stringify(msg)}`);
          return;
        }

        this.resetMovesTo(msg.move.index);
        this.watcher.onError(t('error-invalid-action', { reason: this.invalidMoveReason(parts[1]) }));
        break;

      case 'count-error':
        this.isCounting = false;
        this.watcher.onError(t('count-failed', { reason: msg.message }));
        break;

      default:
        this.watcher.onError(msg.message);
    }
  }

  invalidMoveReason(r) {
    switch(r) {
      case 'not-in-progress':
        return t('reason-not-in-progress');
      case 'invalid-stone':
        return t('reason-invalid-stone');
      case 'no-more-stones':
        return t('reason-no-more-stones');
      case 'invalid-move':
        return t('reason-invalid-data');
      case 'sealed':
        return t('reason-sealed');
      default:
        return null;
    }
  }

  resetMovesTo(index) {
    while(this.game.moves.length > index)
      this.game.takeBack();
  }

  onWsOpen(e) {
    console.log("onWsOpen:");
    console.log(e);
    this.sendWatch(this.confirmedMoves);
    this.sendStatus();
    this.reportVisibility();
    this.sendPing();
    this.reportSeen();
  }

  onWsClose(e) {
    console.log("onWsClose:");
    console.log(e);
    //this.reset();
  }

  onWsError(e) {
    console.log("onWsError:");
    console.log(e);
  }

  onNoticeMessage(msg) {
    const attempt = this.noticeAttempt;
    // Only the stone this page placed can produce a warning. Stored read-back, reconnects,
    // duplicate results and a previous turn's late results stay silent.
    if (!attempt || this.disposed || msg.revision !== this.revision || msg.moveCount !== this.confirmedMoves ||
        attempt.revision !== msg.revision || attempt.moveCount !== msg.moveCount || attempt.warned) return;
    if (['not-enabled', 'failed', 'unconfirmed'].includes(msg.status)) {
      attempt.warned = true;
      if (this.watcher.onNoticeProblem) this.watcher.onNoticeProblem(msg);
    }
  }

  reportVisibility() {
    const visible = !this.frozen && document.visibilityState === 'visible';
    this.send({ code: 'visibility', visible });
    if (visible) { this.sendPing(); this.reportSeen(); }
  }

  async reportSeen() {
    const revision = this.revision, count = this.confirmedMoves;
    if (this.watcher.afterDraw) await this.watcher.afterDraw();
    if (this.disposed || this.frozen || document.visibilityState !== 'visible' || revision !== this.revision || count !== this.confirmedMoves || count !== this.game.moves.length) return;
    this.send({ code: 'seen', moveCount: count, revision });
  }

  close() {
    this.disposed = true;
    this.freeze();
    document.removeEventListener('visibilitychange', this.lifecycle);
    document.removeEventListener('freeze', this.freeze);
    document.removeEventListener('resume', this.resume);
    window.removeEventListener('pagehide', this.freeze);
    window.removeEventListener('pageshow', this.resume);
    this.game.watchers = this.game.watchers.filter(w => w !== this);
    this.ws.onmessage = this.ws.onclose = this.ws.onerror = null;
    this.ws.close();
  }

  #wsUri() {
    const loc = window.location;
    const protocol = (loc.protocol === "https:" ? "wss:" : "ws:");
    return `${protocol}//${loc.host}/ws/games/${this.game.id}`;
  }
}
